import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';

/**
 * Qué se le envió de verdad a cada deudor, por día y por línea.
 *
 * 🔴 SE CUENTAN DEUDORES, NO MENSAJES. Es todo el cambio del 16/09/2026. Antes
 * se contaban mensajes y la «tasa de respuesta» salía del 225%: un deudor que
 * contesta «hola», «vale», «¿cuándo?» y «gracias» valía cuatro respuestas
 * contra un envío. Ahora cada deudor cuenta una vez al día en cada columna, le
 * escribas diez veces o conteste ocho.
 *
 * 🔴 Y ES LO ENVIADO, NO LO VALIDADO. La fuente son las interacciones, no las
 * tareas que selló el despachador: un mensaje sellado que nunca salió no lo vio
 * nadie. Esa diferencia no es pequeña —el 16/09 se sellaron 48 primeros
 * contactos y salieron 26—, y confundirlas era la mitad de lo que no cuadraba.
 *
 * Esta pantalla tenía además una comparativa entre líneas y cuatro cifras de
 * cabecera. Se quitaron: decían lo mismo con otra unidad, y tener dos unidades
 * a la vez es lo que hacía imposible cuadrar nada. Queda una tabla.
 */

const RANGOS = [
  { dias: 7, nombre: '7 días' },
  { dias: 14, nombre: '14 días' },
  { dias: 30, nombre: '30 días' },
];

const fechaCorta = (dia) => {
  const [a, m, d] = dia.split('-');
  return new Date(Date.UTC(+a, +m - 1, +d)).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
};

/**
 * Los cubos del EMBUDO. «Respuesta» no está aquí a propósito: esa columna no
 * cuenta lo que mandamos nosotros contestando, sino **los deudores que nos han
 * escrito**, que es lo que se quiere mirar. Va como número suelto, sin sellado
 * ni aprobado, porque una respuesta que entra no se sella ni se aprueba.
 */
const CUBOS = [
  { clave: 'primero', titulo: '1er msg' },
  { clave: 'segundo', titulo: '2º msg' },
  { clave: 'tercero', titulo: '3er msg' },
  { clave: 'seguimiento', titulo: 'Seguimiento' },
];

/**
 * Las tres magnitudes de una celda: sellado · aprobado · enviado.
 *
 * 🔴 Se pintan juntas A PROPÓSITO. Separadas en tres tablas no se pueden restar,
 * y la resta es justo lo que se quiere ver: un 21 · 14 · 11 dice que de 21 que
 * el policía reservó solo llegaron 11 al teléfono. En ámbar cuando se pierde
 * algo por el camino, que es lo que hay que mirar.
 */
const Trio = ({ s = 0, a = 0, e = 0 }) => {
  if (!s && !a && !e) return <td className='px-3 py-2 text-right text-slate-200'>·</td>;
  const pierde = e < s;
  return (
    <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap'>
      <span className='text-slate-400'>{s}</span>
      <span className='text-slate-300 mx-1'>·</span>
      <span className='text-slate-500'>{a}</span>
      <span className='text-slate-300 mx-1'>·</span>
      <span className={pierde ? 'font-semibold text-amber-700' : 'font-semibold text-slate-900'}>{e}</span>
    </td>
  );
};

// Las clases van literales: Tailwind purga lo que no encuentre escrito tal cual.
const Celda = ({ children, apagada = false, fuerte = false }) => (
  <td
    className={`px-3 py-2 text-right tabular-nums ${
      fuerte ? 'font-medium text-slate-900' : apagada ? 'text-slate-300' : 'text-slate-600'
    }`}
  >
    {children}
  </td>
);

const KpiDeControl = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [dias, setDias] = useState(14);
  const [linea, setLinea] = useState('todas');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/wa-dispatch/kpis?dias=${dias}`);
      setDatos(data.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (cargando && !datos) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 px-6 py-8 mb-6'>
        <p className='text-sm text-slate-500'>Cargando la tabla…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-white rounded-lg border border-red-200 px-6 py-6 mb-6'>
        <p className='text-sm text-red-700'>{error}</p>
      </div>
    );
  }

  const todas = datos?.filas || [];
  const filas = linea === 'todas' ? todas : todas.filter((f) => f.phone_id === linea);
  const lineasConDatos = [...new Set(todas.map((f) => f.phone_id))].sort();
  const hayFueraDeSecuencia = filas.some((f) => f.fuera_de_secuencia > 0);

  const todaLaTabla = datos?.tabla || [];
  const tabla = linea === 'todas' ? todaLaTabla : todaLaTabla.filter((f) => f.phone_id === linea);

  return (
    <div className='bg-white rounded-lg border border-slate-200 mb-6'>
      <div className='px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-base font-semibold text-slate-900'>Qué se envió cada día, por línea</h2>
          <p className='text-sm text-slate-500 mt-0.5'>
            Deudores, no mensajes: diez mensajes al mismo deudor cuentan como uno.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <select
            value={linea}
            onChange={(e) => setLinea(e.target.value)}
            className='rounded-lg border border-slate-300 text-sm px-2 py-1.5 text-slate-700'
          >
            <option value='todas'>Todas las líneas</option>
            {lineasConDatos.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <div className='inline-flex rounded-lg border border-slate-300 overflow-hidden'>
            {RANGOS.map((r) => (
              <button
                key={r.dias}
                type='button'
                onClick={() => setDias(r.dias)}
                className={`px-3 py-1.5 text-sm ${
                  dias === r.dias ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {r.nombre}
              </button>
            ))}
          </div>
          <button
            type='button'
            onClick={cargar}
            className='rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50'
            title='Actualizar'
          >
            <ArrowPathIcon className='w-4 h-4' />
          </button>
        </div>
      </div>

      {/* 🔴 LA TABLA: el mismo contacto en sus tres momentos, en la misma fila.
          Entre sellar y enviar hay DOS escalones —la revisión humana y el brazo—
          y en los dos se pierden mensajes sin que nada lo diga. El 24/09/2026:
          274 sellados, 231 aprobados, 225 enviados. Esos 49 no salían en ninguna
          pantalla, y por eso los números nunca cuadraban con lo que se veía.

          Las tres se clasifican por la CONVERSACIÓN, no por el tipo de tarea ni
          por la categoría del sello (ver `posicion.js` en el backend): si cada
          columna usara su criterio, restarlas mentiría. */}
      <div className='px-6 py-4 border-b border-slate-200'>
        <h3 className='text-sm font-medium text-slate-700 mb-1'>Sellado → aprobado → enviado</h3>
        <p className='text-xs text-slate-500 mb-3'>
          Cada celda son tres números:{' '}
          <span className='text-slate-400'>lo que selló el policía</span> ·{' '}
          <span className='text-slate-500'>lo aprobado para mandar</span> ·{' '}
          <span className='font-semibold text-slate-900'>lo que salió de verdad por el brazo</span>. En{' '}
          <span className='font-semibold text-amber-700'>ámbar</span> cuando llegó menos de lo que se
          reservó. <strong>Respuestas</strong> es aparte: los deudores que <em>nos</em> escribieron.
          Un deudor cuenta una vez por línea y día, mande un mensaje o catorce. El segundo número
          cuenta solo lo que se aprobó <em>enviando</em>: cuando el agente propone no mandar nada —y
          se le aprueba— no entra, porque ahí no hay nada que el brazo pueda entregar.
        </p>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-xs uppercase tracking-wide text-slate-400 text-left border-b border-slate-200'>
                <th className='px-3 py-2 font-medium'>Día</th>
                <th className='px-3 py-2 font-medium'>Línea</th>
                {CUBOS.map((c) => (
                  <th key={c.clave} className='px-3 py-2 font-medium text-right'>
                    {c.titulo}
                  </th>
                ))}
                <th className='px-3 py-2 font-medium text-right border-l border-slate-200'>
                  Respuestas
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {tabla.map((f) => (
                <tr key={`t|${f.phone_id}|${f.dia}`}>
                  <td className='px-3 py-2 text-slate-500 whitespace-nowrap'>{fechaCorta(f.dia)}</td>
                  <td className='px-3 py-2 text-slate-800 whitespace-nowrap'>
                    {f.nombre}
                    {/* Dos líneas pueden compartir `name` con topes distintos, así
                        que cuando el perfil no es el nombre se enseñan los dos. */}
                    {f.nombre !== f.phone_id && (
                      <span className='ml-2 text-xs text-slate-400'>{f.phone_id}</span>
                    )}
                  </td>
                  {CUBOS.map((c) => (
                    <Trio
                      key={c.clave}
                      s={f.sellado?.[c.clave]}
                      a={f.aprobado?.[c.clave]}
                      e={f.enviado?.[c.clave]}
                    />
                  ))}
                  {/* Deudores que escribieron, no mensajes: quien manda catorce
                      globos seguidos cuenta una vez. */}
                  <td
                    className={`px-3 py-2 text-right tabular-nums border-l border-slate-200 ${
                      f.respuestas ? 'font-semibold text-slate-900' : 'text-slate-200'
                    }`}
                  >
                    {f.respuestas || '·'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!tabla.length && (
            <p className='py-8 text-sm text-slate-500'>No hay nada sellado ni enviado en este rango.</p>
          )}
        </div>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='text-xs uppercase tracking-wide text-slate-400 text-left border-b border-slate-200'>
              <th className='px-3 py-2 font-medium'>Día</th>
              <th className='px-3 py-2 font-medium'>Línea</th>
              {/* Sellar reserva el cupo; enviar lo gasta. Entre las dos cosas se
                  cuela lo que se aprueba tarde, lo que se rechaza y lo que no
                  llega a salir, y esa resta no estaba en ninguna pantalla. */}
              <th className='px-3 py-2 font-medium text-right'>Sellado</th>
              <th className='px-3 py-2 font-medium text-right'>de frío</th>
              <th className='px-3 py-2 font-medium text-right border-l border-slate-100'>Enviados</th>
              <th className='px-3 py-2 font-medium text-right border-l border-slate-100'>1er msg</th>
              <th className='px-3 py-2 font-medium text-right'>2º msg</th>
              <th className='px-3 py-2 font-medium text-right'>3er msg</th>
              {hayFueraDeSecuencia && <th className='px-3 py-2 font-medium text-right'>4º+</th>}
              <th className='px-3 py-2 font-medium text-right'>Seguimiento</th>
              <th className='px-3 py-2 font-medium text-right border-l border-slate-100'>Respuesta</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filas.map((f) => (
              <tr key={`${f.phone_id}|${f.dia}`}>
                <td className='px-3 py-2 text-slate-500 whitespace-nowrap'>{fechaCorta(f.dia)}</td>
                <td className='px-3 py-2 text-slate-800 whitespace-nowrap'>
                  {f.nombre}
                  {f.estado !== 'active' && (
                    <span className='ml-2 text-xs text-amber-700'>{f.estado}</span>
                  )}
                </td>
                <Celda apagada={!f.sellado}>{f.sellado}</Celda>
                <Celda apagada={!f.sellado_frio}>{f.sellado_frio}</Celda>
                <td className='px-3 py-2 text-right tabular-nums font-medium text-slate-900 border-l border-slate-100'>
                  {f.enviados}
                </td>
                <Celda apagada={!f.primero}>{f.primero}</Celda>
                <Celda apagada={!f.segundo}>{f.segundo}</Celda>
                <Celda apagada={!f.tercero}>{f.tercero}</Celda>
                {hayFueraDeSecuencia && (
                  <Celda apagada={!f.fuera_de_secuencia}>{f.fuera_de_secuencia}</Celda>
                )}
                <Celda apagada={!f.seguimiento}>{f.seguimiento}</Celda>
                <Celda fuerte={f.respuesta > 0} apagada={!f.respuesta}>
                  {f.respuesta}
                </Celda>
              </tr>
            ))}
          </tbody>
        </table>
        {!filas.length && (
          <p className='px-6 py-8 text-sm text-slate-500'>
            No salió ni un mensaje en este rango.
          </p>
        )}
      </div>

      {/* 🔴 La segunda tabla: lo que cada línea HA HECHO al lado de lo que TIENE
          ESPERANDO. Sin partir por día, porque lo pendiente no tiene día: es el
          estado de ahora mismo. Las dos mitades usan el mismo criterio —la
          posición sale de la conversación— para poder leerse en la misma fila. */}
      <div className='px-6 py-4 border-t border-slate-200'>
        <h3 className='text-sm font-medium text-slate-700 mb-1'>Cada línea: lo hecho y lo que espera</h3>
        <p className='text-xs text-slate-500 mb-3'>
          A la izquierda lo que salió en el rango. A la derecha lo que tiene en cola ahora mismo.
        </p>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-xs uppercase tracking-wide text-slate-400 text-left border-b border-slate-200'>
                <th className='px-3 py-2 font-medium'>Línea</th>
                <th className='px-3 py-2 font-medium text-right'>Enviados</th>
                <th className='px-3 py-2 font-medium text-right'>1er msg</th>
                <th className='px-3 py-2 font-medium text-right'>2º msg</th>
                <th className='px-3 py-2 font-medium text-right'>3er msg</th>
                <th className='px-3 py-2 font-medium text-right'>Seguim.</th>
                <th className='px-3 py-2 font-medium text-right'>Respuesta</th>
                <th className='px-3 py-2 font-medium text-right border-l border-slate-200 bg-slate-50'>
                  Espera 1er
                </th>
                <th className='px-3 py-2 font-medium text-right bg-slate-50'>Espera 2º/3er</th>
                <th className='px-3 py-2 font-medium text-right bg-slate-50'>Espera seguim.</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {(datos?.por_linea || []).map((l) => (
                <tr key={l.phone_id}>
                  <td className='px-3 py-2 text-slate-800 whitespace-nowrap'>
                    {l.nombre}
                    {l.estado !== 'active' && (
                      <span className='ml-2 text-xs text-amber-700'>{l.estado}</span>
                    )}
                  </td>
                  <Celda fuerte>{l.enviados}</Celda>
                  <Celda apagada={!l.primero}>{l.primero}</Celda>
                  <Celda apagada={!l.segundo}>{l.segundo}</Celda>
                  <Celda apagada={!l.tercero}>{l.tercero}</Celda>
                  <Celda apagada={!l.seguimiento}>{l.seguimiento}</Celda>
                  <Celda apagada={!l.respuesta}>{l.respuesta}</Celda>
                  <td className='px-3 py-2 text-right tabular-nums border-l border-slate-200 bg-slate-50 text-slate-600'>
                    {l.pendiente_primero || '—'}
                  </td>
                  <td className='px-3 py-2 text-right tabular-nums bg-slate-50 font-medium text-slate-900'>
                    {l.pendiente_repeticion || '—'}
                  </td>
                  <td className='px-3 py-2 text-right tabular-nums bg-slate-50 text-slate-600'>
                    {l.pendiente_seguimiento || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Los primeros contactos sin línea NO se reparten entre las filas: a un
            deudor con el que no ha hablado nadie no le corresponde ninguna línea
            todavía. Se dice el número, que es lo que hace falta saber. */}
        <p className='text-xs text-slate-500 mt-3'>
          Además hay <span className='font-medium text-slate-700'>{datos?.pendiente_sin_linea?.primeros ?? 0}</span>{' '}
          primeros contactos esperando <span className='font-medium'>sin línea asignada</span>: no cuentan en
          ninguna fila porque la línea la elige el despachador cuando hay hueco.
          {datos?.pendiente_sin_linea?.conversacion > 0 && (
            <>
              {' '}Y {datos.pendiente_sin_linea.conversacion} con conversación pero sin línea guardada, del motor
              viejo.
            </>
          )}
        </p>
      </div>

      {/* Las dos cosas que hay que saber para leerla sin equivocarse. Van en la
          pantalla y no en el código: quien mira la tabla no abre el repo. */}
      <div className='px-6 py-3 border-t border-slate-200 text-xs text-slate-500 space-y-1'>
        <p>
          <span className='font-medium text-slate-600'>Enviados</span> no es la suma de las demás
          columnas: un deudor puede recibir un mensaje en frío y, tras contestar, un seguimiento el
          mismo día. Cuenta una vez en el total y una en cada columna que le toca.
        </p>
        <p>
          <span className='font-medium text-slate-600'>Seguimiento</span> son los deudores que ya
          habían contestado alguna vez antes de ese mensaje. La posición del mensaje en frío se
          deduce de la conversación, no de lo que dijera la tarea.
        </p>
        <p>
          <span className='font-medium text-slate-600'>Sellado</span> es lo que el despachador
          reservó ese día —es lo que consume cupo—, y <span className='font-medium'>de frío</span>{' '}
          la parte que lo gasta de verdad. No se resta de «Enviados» sin más: un sello de las
          19:00 sale al día siguiente, y la categoría del sello no es la posición del mensaje.
        </p>
      </div>
    </div>
  );
};

export default KpiDeControl;
